import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackTrialStarted } from "@/lib/ads-conversions";

// Featured Creator plan — monthly. No coupon → 7-day trial activates.
export const FEATURED_PRICE_ID = "price_1T6Q64Br2hTQtpzMwOESsbBM";

const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 800;

const makeRequestId = () => {
  const rand = Math.random().toString(36).slice(2, 8);
  return `trial_${Date.now().toString(36)}_${rand}`;
};

export interface UseTrialCheckoutOptions {
  /** Stringa di log per identificare il caller (es: "TrialCTABanner") */
  source: string;
}

export interface UseTrialCheckoutResult {
  loading: boolean;
  retrying: { attempt: number; total: number } | null;
  errorMsg: string | null;
  errorRequestId: string | null;
  start: () => Promise<void>;
}

/**
 * Hook condiviso per avviare il trial 7 giorni con verifica Stripe,
 * retry automatico con backoff esponenziale e logging per request ID.
 */
export function useTrialCheckout({ source }: UseTrialCheckoutOptions): UseTrialCheckoutResult {
  const { user } = useAuth();
  const { toast } = useToast();
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<{ attempt: number; total: number } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorRequestId, setErrorRequestId] = useState<string | null>(null);

  const t = (it: string, en: string, es: string) =>
    lang === "it" ? it : lang === "es" ? es : en;

  const attemptCheckout = useCallback(
    async (requestId: string, attempt: number) => {
      const startedAt = performance.now();
      console.info(`[${source}] →`, {
        requestId,
        attempt,
        step: "invoke",
        priceId: FEATURED_PRICE_ID,
        userId: user?.id,
      });

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId: FEATURED_PRICE_ID,
          mode: "trial",
          requestId,
          attempt,
          successUrl: `${window.location.origin}/?success=true`,
          cancelUrl: `${window.location.origin}/dashboard`,
        },
        headers: { "x-request-id": requestId },
      });

      const durationMs = Math.round(performance.now() - startedAt);
      console.info(`[${source}] ←`, {
        requestId,
        attempt,
        durationMs,
        trialDays: data?.trial_period_days,
        hasCoupon: data?.has_coupon,
        hasUrl: Boolean(data?.url),
        invokeError: error?.message,
        serverError: data?.error,
      });

      if (error) {
        // Try to get richer details from the function response body
        let serverDetail: any = null;
        try {
          // @ts-ignore — context may include response
          const ctx = (error as any).context;
          if (ctx?.body) serverDetail = JSON.parse(await ctx.text());
        } catch { /* ignore */ }
        console.error(`[${source}] invoke error detail`, { requestId, serverDetail, error });
        const stripeMsg = serverDetail?.error;
        const stripeMeta = serverDetail?.stripe;
        const detailed = stripeMsg
          ? `${stripeMsg}${stripeMeta?.code ? ` (code: ${stripeMeta.code})` : ""}`
          : error.message || "invoke_failed";
        const err = new Error(detailed);
        (err as any).stripe = stripeMeta;
        throw err;
      }
      if (data?.error) {
        const err = new Error(
          data.stripe?.code ? `${data.error} (code: ${data.stripe.code})` : data.error
        );
        (err as any).stripe = data.stripe;
        throw err;
      }
      if (data?.trial_period_days !== 7 || data?.has_coupon) {
        console.error(`[${source}] trial not configured correctly`, { requestId, data });
        throw new Error("trial_verification_failed");
      }
      if (!data?.url) throw new Error("missing_checkout_url");
      return data.url as string;
    },
    [source, user?.id]
  );

  const start = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setErrorMsg(null);
    setErrorRequestId(null);

    let lastError: unknown = null;
    let lastRequestId = "";

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      const requestId = makeRequestId();
      lastRequestId = requestId;
      try {
        if (attempt > 1) {
          setRetrying({ attempt, total: MAX_ATTEMPTS });
          const delay = BASE_DELAY_MS * Math.pow(2, attempt - 2);
          await new Promise((r) => setTimeout(r, delay));
        }
        const url = await attemptCheckout(requestId, attempt);
        setRetrying(null);
        // Google Ads conversion — trial started (fire before redirect)
        try { trackTrialStarted(source); } catch { /* noop */ }
        window.location.href = url;
        return;
      } catch (e) {
        lastError = e;
        const reason = e instanceof Error ? e.message : "unknown";
        console.warn(`[${source}] attempt ${attempt}/${MAX_ATTEMPTS} failed`, { requestId, reason });
      }
    }

    setRetrying(null);
    const reason = lastError instanceof Error ? lastError.message : "unknown";
    console.error(`[${source}] checkout failed after retries`, {
      lastRequestId,
      reason,
      stripe: (lastError as any)?.stripe,
      attempts: MAX_ATTEMPTS,
    });
    const knownGeneric =
      reason === "trial_verification_failed"
        ? t(
            "Il trial di 7 giorni non risulta attivato lato Stripe. Riprova: nessun addebito verrà effettuato.",
            "The 7-day trial was not confirmed by Stripe. Please retry — no charge will be made.",
            "Stripe no confirmó la prueba de 7 días. Reinténtalo — no se cobrará nada."
          )
        : reason === "missing_checkout_url"
        ? t(
            "Stripe non ha restituito un link di pagamento valido. Riprova.",
            "Stripe did not return a valid checkout link. Please retry.",
            "Stripe no devolvió un enlace válido. Reinténtalo."
          )
        : reason === "invoke_failed" || reason === "unknown" || reason.includes("Failed to fetch")
        ? t(
            "Impossibile contattare Stripe per avviare il trial. Controlla la connessione e riprova.",
            "Could not reach Stripe to start the trial. Check your connection and retry.",
            "No se pudo contactar a Stripe. Revisa tu conexión y reinténtalo."
          )
        : null;
    // If Stripe returned a specific message, show it (more useful for debugging real causes).
    const msg = knownGeneric ?? `${t("Stripe ha rifiutato il checkout", "Stripe rejected the checkout", "Stripe rechazó el pago")}: ${reason}`;
    setErrorMsg(msg);
    setErrorRequestId(lastRequestId);
    toast({
      title: t("Errore trial", "Trial error", "Error de prueba"),
      description: `${msg}\nID: ${lastRequestId}`,
      variant: "destructive",
    });
    setLoading(false);
  }, [attemptCheckout, source, toast, user, lang]);

  return { loading, retrying, errorMsg, errorRequestId, start };
}
